import React, { useState } from 'react';

const V1 = '#7F00FF', V2 = '#B163FF', TEAL = '#3B82F6', GREEN = '#2ECC71', RED = '#F04155';
const MODES = [
  { key: 'build', label: 'Build a Graph' },
  { key: 'bfs', label: 'BFS Traversal' },
  { key: 'dfs', label: 'DFS Traversal' },
  { key: 'cycle', label: 'Detect a Cycle' },
  { key: 'quiz', label: 'Quick Quiz' },
];

const CodePanel = ({ title, lines, active }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '9px 14px', background: 'var(--bg3)', borderBottom: '1px solid var(--border)', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>
      {title || 'Java'}
    </div>
    <pre style={{ margin: 0, padding: '12px 0', background: '#0E0B18', fontFamily: "'Fira Code','Consolas',monospace", fontSize: 12.5, lineHeight: 1.9, overflowX: 'auto' }}>
      {lines.map((line, i) => (
        <div key={i} style={{
          padding: '0 16px', color: i === active ? '#fff' : '#B7ACD6',
          background: i === active ? `${V1}45` : 'transparent',
          borderLeft: `3px solid ${i === active ? V2 : 'transparent'}`,
          whiteSpace: 'pre',
        }}>{line || ' '}</div>
      ))}
    </pre>
  </div>
);

/* ─── Graph helpers ──────────────────────────────────────────────── */
const DEFAULT_NODES = [0, 1, 2, 3, 4, 5];
const DEFAULT_EDGES = [[0, 1], [0, 2], [1, 3], [2, 3], [3, 4], [4, 5]];
const buildAdjacency = (nodes, edges, directed) => {
  const adj = {};
  nodes.forEach(n => { adj[n] = []; });
  edges.forEach(([a, b]) => {
    if (adj[a]) adj[a].push(b);
    if (!directed && adj[b]) adj[b].push(a);
  });
  return adj;
};

/* ─── SVG graph view, circular layout ────────────────────────────── */
const GraphView = ({ nodes, edges, directed, active, visited, activeEdges = [] }) => {
  const n = nodes.length;
  const R = 82, cx = 110, cy = 110;
  const pos = nodes.map((_, i) => {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    return { x: cx + R * Math.cos(angle), y: cy + R * Math.sin(angle) };
  });
  const idx = val => nodes.indexOf(val);
  const edgeActive = (a, b) => activeEdges.some(([x, y]) => (x === a && y === b) || (!directed && x === b && y === a));
  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingBottom: 10 }}>
      <svg width="220" height="220" viewBox="0 0 220 220">
        {directed && (
          <defs>
            <marker id="gpArrow" markerWidth="8" markerHeight="8" refX="15" refY="4" orient="auto">
              <polygon points="0 0, 8 4, 0 8" style={{ fill: 'var(--muted)' }} />
            </marker>
          </defs>
        )}
        {edges.map(([a, b], i) => {
          const ai = idx(a), bi = idx(b);
          if (ai === -1 || bi === -1) return null;
          const hl = edgeActive(a, b);
          return (
            <line key={i} x1={pos[ai].x} y1={pos[ai].y} x2={pos[bi].x} y2={pos[bi].y}
              style={{ stroke: hl ? V1 : 'var(--border-strong)' }} strokeWidth={hl ? 3 : 1.5}
              markerEnd={directed ? 'url(#gpArrow)' : undefined} />
          );
        })}
        {nodes.map((val, i) => {
          const isActive = active && active.includes(val);
          const isVisited = visited && visited.includes(val);
          return (
            <g key={val}>
              <circle cx={pos[i].x} cy={pos[i].y} r="16" style={{ fill: isActive ? V1 : isVisited ? 'var(--bg2)' : 'var(--bg3)', stroke: isActive ? V1 : isVisited ? GREEN : 'var(--border)' }} strokeWidth="1.5" />
              <text x={pos[i].x} y={pos[i].y + 5} textAnchor="middle" fontSize="13" fontWeight="800" style={{ fill: isActive ? '#fff' : 'var(--text)', fontFamily: "'Inter',sans-serif" }}>{val}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

const smallInput = {
  width: 70, padding: '8px 10px', borderRadius: 9, border: '1px solid var(--border)',
  background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
};
const smallBtn = (bg = V1, disabled = false) => ({
  padding: '8px 14px', borderRadius: 9, border: 'none', cursor: disabled ? 'default' : 'pointer',
  background: disabled ? 'var(--bg3)' : bg, color: disabled ? 'var(--muted)' : '#fff',
  fontSize: 12.5, fontWeight: 700, fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const ghostBtn = disabled => ({
  padding: '8px 14px', borderRadius: 9, cursor: disabled ? 'default' : 'pointer',
  border: `1.5px solid ${disabled ? 'var(--border)' : V1}`, background: 'transparent',
  color: disabled ? 'var(--muted)' : V1, fontSize: 12.5, fontWeight: 700,
  fontFamily: "'Satoshi','DM Sans',sans-serif", opacity: disabled ? 0.6 : 1,
});
const StepNav = ({ step, total, onPrev, onNext }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <button onClick={onPrev} disabled={step === 0} style={ghostBtn(step === 0)}>← Prev</button>
    <button onClick={onNext} disabled={step === total - 1} style={smallBtn(V1, step === total - 1)}>Next →</button>
    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Step {step + 1} of {total}</span>
  </div>
);
const Chip = ({ children, color = V1 }) => (
  <span style={{
    display: 'inline-flex', padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700,
    background: `${color}15`, color, marginRight: 6, marginBottom: 6, fontFamily: 'monospace',
  }}>{children}</span>
);

/* ─── Build a Graph ──────────────────────────────────────────────── */
const BuildMode = () => {
  const [nodes, setNodes] = useState(DEFAULT_NODES);
  const [edges, setEdges] = useState(DEFAULT_EDGES);
  const [directed, setDirected] = useState(false);
  const [a, setA] = useState('');
  const [b, setB] = useState('');

  const addEdge = () => {
    const na = Number(a), nb = Number(b);
    if (isNaN(na) || isNaN(nb)) return;
    setNodes(n => (n.includes(na) ? n : [...n, na]));
    setNodes(n => (n.includes(nb) ? n : [...n, nb]));
    setEdges(e => [...e, [na, nb]]);
    setA(''); setB('');
  };
  const reset = () => { setNodes([]); setEdges([]); };
  const adj = buildAdjacency(nodes, edges, directed);

  const code = {
    lines: [
      'Map<Integer, List<Integer>> adj = new HashMap<>();',
      'for (int[] edge : edges) {',
      '    adj.computeIfAbsent(edge[0], k -> new ArrayList<>()).add(edge[1]);',
      '    adj.computeIfAbsent(edge[1], k -> new ArrayList<>()).add(edge[0]);   // omit for directed',
      '}',
    ],
    active: 2,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GraphView nodes={nodes} edges={edges} directed={directed} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--muted)', fontWeight: 700 }}>
            <input type="checkbox" checked={directed} onChange={e => setDirected(e.target.checked)} /> Directed
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <input value={a} onChange={e => setA(e.target.value)} style={smallInput} placeholder="from" />
            <span style={{ color: 'var(--muted)' }}>→</span>
            <input value={b} onChange={e => setB(e.target.value)} style={smallInput} placeholder="to" />
            <button onClick={addEdge} style={smallBtn(V1)}>Add Edge</button>
            <button onClick={reset} style={smallBtn(RED)}>Clear</button>
          </div>
          <div>
            {nodes.map(n => <Chip key={n} color={TEAL}>{n}: [{adj[n].join(', ')}]</Chip>)}
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--muted)', margin: 0 }}>Each node's chip shows its adjacency list — exactly what Java code actually stores and iterates over.</p>
        </div>
      </div>
      <CodePanel title="Java — Adjacency List" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── BFS Traversal ──────────────────────────────────────────────── */
const BFSMode = () => {
  const [startInput, setStartInput] = useState('0');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const start = Number(startInput);
    if (isNaN(start) || !DEFAULT_NODES.includes(start)) return;
    const adj = buildAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);
    const built = [];
    const visited = new Set([start]);
    const queue = [start];
    built.push({ current: start, queue: [...queue], visited: [...visited], note: `Start at ${start}. Mark visited, enqueue it.` });
    while (queue.length) {
      const node = queue.shift();
      built.push({ current: node, queue: [...queue], visited: [...visited], action: 'dequeue', note: `Dequeue ${node} — visit it.` });
      for (const neighbor of adj[node]) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
          built.push({ current: node, neighbor, queue: [...queue], visited: [...visited], note: `${node}'s neighbor ${neighbor} is unvisited — mark visited, enqueue it.` });
        }
      }
    }
    built.push({ done: true, visited: [...visited] });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;
  const adj = buildAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);

  const code = {
    lines: [
      'Set<Integer> visited = new HashSet<>();',
      'Queue<Integer> queue = new LinkedList<>();',
      'visited.add(start); queue.add(start);',
      'while (!queue.isEmpty()) {',
      '    int node = queue.poll();',
      '    for (int neighbor : adj.get(node)) {',
      '        if (visited.add(neighbor)) {   // true if newly added',
      '            queue.add(neighbor);',
      '        }',
      '    }',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.action === 'dequeue' ? 4 : 7,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} active={cur && !cur.done ? [cur.current] : []} visited={cur ? cur.visited : []} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Start node</span>
            <input value={startInput} onChange={e => setStartInput(e.target.value)} style={smallInput} />
            <button onClick={run} style={smallBtn(V1)}>Run BFS</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          {cur && <Chip color={TEAL}>queue: [{(cur.queue || []).join(', ')}]</Chip>}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {!cur ? 'Default graph: 0-1, 0-2, 1-3, 2-3, 3-4, 4-5. Pick a start node, then Run BFS.' :
              cur.done ? `Queue empty — BFS complete. Visited order explored level by level.` : cur.note}
          </p>
        </div>
      </div>
      <CodePanel title="Java — BFS (Queue)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── DFS Traversal ──────────────────────────────────────────────── */
const DFSMode = () => {
  const [startInput, setStartInput] = useState('0');
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const start = Number(startInput);
    if (isNaN(start) || !DEFAULT_NODES.includes(start)) return;
    const adj = buildAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);
    const built = [];
    const visited = new Set();
    const dfs = node => {
      visited.add(node);
      built.push({ current: node, visited: [...visited], note: `Visit ${node} — mark it visited.` });
      for (const neighbor of adj[node]) {
        if (!visited.has(neighbor)) {
          built.push({ current: node, neighbor, visited: [...visited], note: `${node}'s neighbor ${neighbor} is unvisited — recurse into it.` });
          dfs(neighbor);
        }
      }
    };
    dfs(start);
    built.push({ done: true, visited: [...visited] });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'void dfs(int node, Set<Integer> visited, Map<Integer, List<Integer>> adj) {',
      '    visited.add(node);',
      '    // visit(node) here',
      '    for (int neighbor : adj.get(node)) {',
      '        if (!visited.contains(neighbor)) {',
      '            dfs(neighbor, visited, adj);',
      '        }',
      '    }',
      '}',
    ],
    active: !cur || cur.done ? -1 : cur.neighbor !== undefined ? 5 : 1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} active={cur && !cur.done ? [cur.current] : []} visited={cur ? cur.visited : []} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--muted)' }}>Start node</span>
            <input value={startInput} onChange={e => setStartInput(e.target.value)} style={smallInput} />
            <button onClick={run} style={smallBtn(V1)}>Run DFS</button>
          </div>
          {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {!cur ? 'Same default graph. Pick a start node, then Run DFS — notice it dives deep before backtracking, unlike BFS\'s level-by-level spread.' :
              cur.done ? 'Recursion fully unwound — DFS complete.' : cur.note}
          </p>
        </div>
      </div>
      <CodePanel title="Java — DFS (Recursive)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Detect a Cycle ─────────────────────────────────────────────── */
const CycleMode = () => {
  const [steps, setSteps] = useState(null);
  const [step, setStep] = useState(0);

  const run = () => {
    const adj = buildAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);
    const built = [];
    const visited = new Set();
    let cycleFound = false;
    const dfs = (node, parent) => {
      if (cycleFound) return;
      visited.add(node);
      built.push({ current: node, parent, visited: [...visited], note: `Visit ${node} (came from ${parent === -1 ? 'nowhere — this is the start' : parent}).` });
      for (const neighbor of adj[node]) {
        if (cycleFound) return;
        if (!visited.has(neighbor)) {
          built.push({ current: node, neighbor, edge: [node, neighbor], visited: [...visited], note: `Neighbor ${neighbor} unvisited — recurse.` });
          dfs(neighbor, node);
        } else if (neighbor !== parent) {
          cycleFound = true;
          built.push({ current: node, neighbor, edge: [node, neighbor], visited: [...visited], cycle: true, note: `Neighbor ${neighbor} is ALREADY visited, and it isn't where we came from (parent is ${parent}) — that's a back edge. Cycle detected!` });
        }
      }
    };
    dfs(0, -1);
    if (!cycleFound) built.push({ done: true, cycle: false });
    setSteps(built); setStep(0);
  };

  const cur = steps ? steps[step] : null;

  const code = {
    lines: [
      'boolean hasCycle(int node, int parent, Set<Integer> visited, Map<Integer, List<Integer>> adj) {',
      '    visited.add(node);',
      '    for (int neighbor : adj.get(node)) {',
      '        if (!visited.contains(neighbor)) {',
      '            if (hasCycle(neighbor, node, visited, adj)) return true;',
      '        } else if (neighbor != parent) {',
      '            return true;   // back edge — a cycle exists',
      '        }',
      '    }',
      '    return false;',
      '}',
    ],
    active: !cur ? -1 : cur.cycle ? 6 : cur.neighbor !== undefined ? 4 : 1,
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.3fr) minmax(0,1fr)', gap: 22 }} className="dsa-playground-grid">
      <div>
        <GraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} active={cur && cur.current !== undefined ? [cur.current] : []} visited={cur ? cur.visited : []} activeEdges={cur && cur.edge ? [cur.edge] : []} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          <button onClick={run} style={smallBtn(V1)}>Detect Cycle (from node 0)</button>
        </div>
        {steps && <StepNav step={step} total={steps.length} onPrev={() => setStep(s => Math.max(0, s - 1))} onNext={() => setStep(s => Math.min(steps.length - 1, s + 1))} />}
        <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: '14px 0 0' }}>
          {!cur ? 'This graph has a 4-node cycle (0-1-3-2-0) plus a tail (3-4-5). Run to watch DFS find the back edge.' :
            cur.done ? 'No cycle found.' : cur.note}
        </p>
      </div>
      <CodePanel title="Java — Cycle Detection (Undirected DFS)" lines={code.lines} active={code.active} />
    </div>
  );
};

/* ─── Quick Quiz ─────────────────────────────────────────────────── */
const buildQuizScenario = () => {
  const adj = buildAdjacency(DEFAULT_NODES, DEFAULT_EDGES, false);
  const current = DEFAULT_NODES[Math.floor(Math.random() * DEFAULT_NODES.length)];
  const options = adj[current];
  const correct = options[Math.floor(Math.random() * options.length)];
  const wrong = DEFAULT_NODES.find(n => n !== current && !options.includes(n));
  const askCorrect = Math.random() < 0.5;   // decided once per scenario, not re-rolled on every render
  const target = askCorrect ? correct : wrong;
  return { current, target, correctAnswer: askCorrect ? 'yes' : 'no' };
};
const QuizMode = () => {
  const [scenario, setScenario] = useState(buildQuizScenario());
  const [answer, setAnswer] = useState(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });

  const pick = choice => {
    if (answer !== null) return;
    setAnswer(choice);
    setScore(s => ({ correct: s.correct + (choice === scenario.correctAnswer ? 1 : 0), total: s.total + 1 }));
  };
  const next = () => { setScenario(buildQuizScenario()); setAnswer(null); };

  return (
    <div>
      <GraphView nodes={DEFAULT_NODES} edges={DEFAULT_EDGES} active={[scenario.current, scenario.target]} />
      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 14 }}>
        Is there a direct edge between node {scenario.current} and node {scenario.target}?
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['yes', 'no'].map(o => {
          const revealed = answer !== null;
          const isCorrect = o === scenario.correctAnswer;
          const isPicked = o === answer;
          let border = 'var(--border)', bg = 'transparent', color = 'var(--text)';
          if (revealed && isCorrect) { border = GREEN; bg = 'rgba(46,204,113,0.1)'; color = '#1e9e57'; }
          else if (revealed && isPicked && !isCorrect) { border = RED; bg = 'rgba(240,65,85,0.08)'; color = RED; }
          return (
            <button key={o} onClick={() => pick(o)} disabled={revealed} style={{
              padding: '10px 20px', borderRadius: 10, border: `1.5px solid ${border}`, background: bg, color,
              fontSize: 13.5, fontWeight: 700, cursor: revealed ? 'default' : 'pointer', fontFamily: "'Satoshi','DM Sans',sans-serif", textTransform: 'capitalize',
            }}>{o}</button>
          );
        })}
      </div>
      {answer !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={next} style={smallBtn(V1)}>Next Question</button>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>Score: {score.correct} / {score.total}</span>
        </div>
      )}
    </div>
  );
};

/* ─── Main playground ────────────────────────────────────────────── */
const GraphPlayground = () => {
  const [mode, setMode] = useState('build');

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
              border: `1.5px solid ${mode === m.key ? V1 : 'var(--border)'}`,
              background: mode === m.key ? V1 : 'transparent',
              color: mode === m.key ? '#fff' : 'var(--muted)',
              fontFamily: "'Satoshi','DM Sans',sans-serif",
            }}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'build' && <BuildMode />}
      {mode === 'bfs' && <BFSMode />}
      {mode === 'dfs' && <DFSMode />}
      {mode === 'cycle' && <CycleMode />}
      {mode === 'quiz' && <QuizMode />}

      <style>{`
        @media (max-width: 640px) {
          .dsa-playground-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default GraphPlayground;
